import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EmailIcon from "@mui/icons-material/Email";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorIcon from "@mui/icons-material/Error";
import {
  generateFakeInvoiceData,
  generateDetailedInvoiceData,
  InvoiceData,
  DetailedInvoiceData,
} from "../../../../utils/fakeInvoiceData";

// Define column keys for sorting
type ColumnKey =
  | "invoiceNumber"
  | "customerName"
  | "invoiceType"
  | "amount"
  | "status"
  | "issueDate"
  | "dueDate";

// Define sort directions
type SortDirection = "asc" | "desc";

// Invoice Modal Component
const InvoiceModal = ({
  isOpen,
  onClose,
  invoiceData,
}: {
  isOpen: boolean;
  onClose: () => void;
  invoiceData: DetailedInvoiceData | null;
}) => {
  if (!invoiceData) return null;

  const getStatusIcon = () => {
    switch (invoiceData.status) {
      case "paid":
        return (
          <CheckCircleIcon fontSize="small" className="mr-1 text-green-600" />
        );
      case "pending":
        return <PendingIcon fontSize="small" className="mr-1 text-amber-500" />;
      case "overdue":
        return <ErrorIcon fontSize="small" className="mr-1 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (invoiceData.status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-amber-100 text-amber-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = () => {
    switch (invoiceData.status) {
      case "paid":
        return "Paid";
      case "pending":
        return "Pending";
      case "overdue":
        const due = new Date(invoiceData.dueDate);
        const today = new Date();

        // Set time to midnight for accurate day comparison
        due.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = today.getTime() - due.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? `${diffDays} days overdue` : "Overdue";
      default:
        return invoiceData.status;
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case "membership":
        return "Membership";
      case "event_ticket":
        return "Event Ticket";
      case "sponsorship":
        return "Sponsorship";
      default:
        return type;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="invoice-dialog-title"
    >
      <DialogTitle id="invoice-dialog-title">
        <div className="flex justify-between items-center">
          <Typography variant="h6">
            Invoice #{invoiceData.invoiceNumber}
          </Typography>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="p-2">
          {/* Chamber Info & Logo */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <Typography variant="h5" className="font-bold mb-1">
                {invoiceData.chamberInfo.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoiceData.chamberInfo.address}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoiceData.chamberInfo.phone}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoiceData.chamberInfo.email}
              </Typography>
            </div>
            <div className="text-right">
              <Typography variant="h4" className="text-gray-300 font-bold">
                INVOICE
              </Typography>
              <Typography variant="h6" className="mb-1">
                #{invoiceData.invoiceNumber}
              </Typography>
              <div
                className={`inline-flex items-center px-3 py-1 rounded-full ${getStatusColor()}`}
              >
                {getStatusIcon()}
                <Typography variant="body2" className="font-medium">
                  {getStatusText()}
                </Typography>
              </div>
            </div>
          </div>

          {/* Bill To & Invoice Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <Typography variant="subtitle2" className="text-gray-600 mb-2">
                BILL TO
              </Typography>
              <Typography variant="body1" className="font-medium">
                {invoiceData.customerInfo.name}
              </Typography>
              <Typography variant="body1">
                {invoiceData.customerInfo.company}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoiceData.customerInfo.address}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoiceData.customerInfo.email}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {invoiceData.customerInfo.phone}
              </Typography>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                  INVOICE DATE
                </Typography>
                <Typography variant="body1">{invoiceData.issueDate}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                  DUE DATE
                </Typography>
                <Typography variant="body1">{invoiceData.dueDate}</Typography>
              </div>
              {invoiceData.paidDate && (
                <>
                  <div>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-600 mb-1"
                    >
                      PAID DATE
                    </Typography>
                    <Typography variant="body1">
                      {invoiceData.paidDate}
                    </Typography>
                  </div>
                  <div>
                    <Typography
                      variant="subtitle2"
                      className="text-gray-600 mb-1"
                    >
                      PAYMENT METHOD
                    </Typography>
                    <Typography variant="body1">
                      {invoiceData.paymentMethod}
                    </Typography>
                  </div>
                </>
              )}
              <div>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                  INVOICE TYPE
                </Typography>
                <Typography variant="body1">
                  {getTypeText(invoiceData.invoiceType)}
                </Typography>
              </div>
              <div>
                <Typography variant="subtitle2" className="text-gray-600 mb-1">
                  AMOUNT DUE
                </Typography>
                <Typography variant="body1" className="font-bold">
                  $
                  {invoiceData.status === "paid"
                    ? "0.00"
                    : invoiceData.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                </Typography>
              </div>
            </div>
          </div>

          <Divider className="mb-6" />

          {/* Invoice Items */}
          <div className="mb-8">
            <Typography variant="h6" className="mb-4">
              Invoice Items
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              className="border rounded"
            >
              <Table size="small">
                <TableHead className="bg-gray-50">
                  <TableRow>
                    <TableCell className="font-medium">Description</TableCell>
                    <TableCell align="right" className="font-medium">
                      Quantity
                    </TableCell>
                    <TableCell align="right" className="font-medium">
                      Unit Price
                    </TableCell>
                    <TableCell align="right" className="font-medium">
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        $
                        {item.unitPrice.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell align="right">
                        $
                        {item.total.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Totals & Payment */}
          <div className="flex flex-col items-end mb-6">
            <div className="w-full md:w-1/2 space-y-2">
              <div className="flex justify-between py-2">
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">
                  $
                  {invoiceData.subtotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </div>
              {invoiceData.tax > 0 && (
                <div className="flex justify-between py-2">
                  <Typography variant="body1">Tax</Typography>
                  <Typography variant="body1">
                    $
                    {invoiceData.tax.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </div>
              )}
              <div className="flex justify-between py-2 border-t border-gray-300">
                <Typography variant="body1" className="font-bold">
                  Total
                </Typography>
                <Typography variant="body1" className="font-bold">
                  $
                  {invoiceData.total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              </div>

              {invoiceData.status === "paid" && (
                <div className="flex justify-between py-2 bg-green-50 px-3 rounded">
                  <Typography
                    variant="body1"
                    className="font-bold text-green-800"
                  >
                    Paid
                  </Typography>
                  <Typography
                    variant="body1"
                    className="font-bold text-green-800"
                  >
                    $
                    {invoiceData.total.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </div>
              )}

              {invoiceData.status !== "paid" && (
                <div className="flex justify-between py-2 bg-amber-50 px-3 rounded">
                  <Typography
                    variant="body1"
                    className="font-bold text-amber-800"
                  >
                    Amount Due
                  </Typography>
                  <Typography
                    variant="body1"
                    className="font-bold text-amber-800"
                  >
                    $
                    {invoiceData.total.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoiceData.notes && (
            <div className="mt-4 p-4 bg-gray-50 rounded">
              <Typography variant="subtitle2" className="text-gray-600 mb-1">
                NOTES
              </Typography>
              <Typography variant="body2">{invoiceData.notes}</Typography>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between mt-8">
            <div className="space-x-2">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                size="small"
              >
                Download
              </Button>
              <Button variant="outlined" startIcon={<EmailIcon />} size="small">
                Email
              </Button>
            </div>
            {/* {invoiceData.status !== "paid" && (
              <Button
                variant="contained"
                color="primary"
                className="bg-cpaGold hover:bg-amber-600"
              >
                Pay Now
              </Button>
            )} */}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ManageInvoices = () => {
  // State for invoice data
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);

  // State for search query
  const [searchQuery, setSearchQuery] = useState("");

  // State for sorting
  const [sortColumn, setSortColumn] = useState<ColumnKey>("issueDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // State for the selected invoice and modal visibility
  const [selectedInvoice, setSelectedInvoice] =
    useState<DetailedInvoiceData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load fake data on component mount
  useEffect(() => {
    const data = generateFakeInvoiceData(50);
    setInvoices(data);
  }, []);

  // Function to calculate days overdue
  const calculateDaysOverdue = (dueDate: string): number => {
    const due = new Date(dueDate);
    const today = new Date();

    // Set time to midnight for accurate day comparison
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Function to get overdue text with days - only for invoices marked as overdue
  const getOverdueText = (invoice: InvoiceData): string => {
    if (invoice.status !== "overdue") {
      return invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1);
    }

    const daysOverdue = calculateDaysOverdue(invoice.dueDate);
    return daysOverdue > 0 ? `${daysOverdue} days overdue` : "Overdue";
  };

  // Handle search
  const handleSearchChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(target.value);
  };

  // Handle sort
  const handleRequestSort = (column: ColumnKey) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortColumn(column);
  };

  // Handle opening the invoice modal
  const handleOpenInvoice = (invoice: InvoiceData) => {
    const detailedInvoice = generateDetailedInvoiceData(invoice);
    setSelectedInvoice(detailedInvoice);
    setIsModalOpen(true);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInvoice(null);
  };

  // Filter invoices based on search query
  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Function to get status color
  const getStatusColor = (status: InvoiceData["status"]) => {
    switch (status) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  // Function to get invoice type display name
  const getInvoiceTypeDisplay = (type: InvoiceData["invoiceType"]) => {
    switch (type) {
      case "membership":
        return "Membership";
      case "event_ticket":
        return "Event Ticket";
      case "sponsorship":
        return "Sponsorship";
      default:
        return type;
    }
  };

  // Sort invoices based on current sort column and direction
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    // Handle special sorting cases
    if (sortColumn === "invoiceNumber") {
      return sortDirection === "asc"
        ? a.invoiceNumber.localeCompare(b.invoiceNumber)
        : b.invoiceNumber.localeCompare(a.invoiceNumber);
    }

    if (sortColumn === "customerName") {
      return sortDirection === "asc"
        ? a.customerName.localeCompare(b.customerName)
        : b.customerName.localeCompare(a.customerName);
    }

    if (sortColumn === "invoiceType") {
      return sortDirection === "asc"
        ? a.invoiceType.localeCompare(b.invoiceType)
        : b.invoiceType.localeCompare(a.invoiceType);
    }

    if (sortColumn === "amount") {
      return sortDirection === "asc"
        ? a.amount - b.amount
        : b.amount - a.amount;
    }

    if (sortColumn === "status") {
      return sortDirection === "asc"
        ? a.status.localeCompare(b.status)
        : b.status.localeCompare(a.status);
    }

    if (sortColumn === "issueDate" || sortColumn === "dueDate") {
      const dateA = new Date(
        sortColumn === "issueDate" ? a.issueDate : a.dueDate
      );
      const dateB = new Date(
        sortColumn === "issueDate" ? b.issueDate : b.dueDate
      );
      return sortDirection === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    return 0;
  });

  return (
    <div className="">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Paper className="p-4 flex items-center">
          <div className="mr-4 bg-blue-100 p-2 rounded">
            <ReceiptIcon className="text-blue-600" />
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Total Invoices
            </Typography>
            <Typography variant="h6">{invoices.length}</Typography>
          </div>
        </Paper>

        <Paper className="p-4 flex items-center">
          <div className="mr-4 bg-green-100 p-2 rounded">
            <CheckCircleIcon className="text-green-600" />
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Paid
            </Typography>
            <Typography variant="h6">
              {invoices.filter((inv) => inv.status === "paid").length}
            </Typography>
          </div>
        </Paper>

        <Paper className="p-4 flex items-center">
          <div className="mr-4 bg-amber-100 p-2 rounded">
            <PendingIcon className="text-amber-500" />
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Pending
            </Typography>
            <Typography variant="h6">
              {invoices.filter((inv) => inv.status === "pending").length}
            </Typography>
          </div>
        </Paper>

        <Paper className="p-4 flex items-center">
          <div className="mr-4 bg-red-100 p-2 rounded">
            <ErrorIcon className="text-red-600" />
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">
              Overdue
            </Typography>
            <Typography variant="h6">
              {invoices.filter((inv) => inv.status === "overdue").length}
            </Typography>
          </div>
        </Paper>
      </div>

      {/* Invoices table */}
      <TableContainer component={Paper} className="mb-8">
        <Table aria-label="invoices table">
          <TableHead className="bg-gray-50">
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "invoiceNumber"}
                  direction={
                    sortColumn === "invoiceNumber" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("invoiceNumber")}
                  className="font-medium"
                >
                  Invoice Number
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "customerName"}
                  direction={
                    sortColumn === "customerName" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("customerName")}
                  className="font-medium"
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "invoiceType"}
                  direction={
                    sortColumn === "invoiceType" ? sortDirection : "asc"
                  }
                  onClick={() => handleRequestSort("invoiceType")}
                  className="font-medium"
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "issueDate"}
                  direction={sortColumn === "issueDate" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("issueDate")}
                  className="font-medium"
                >
                  Issue Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "dueDate"}
                  direction={sortColumn === "dueDate" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("dueDate")}
                  className="font-medium"
                >
                  Due Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "amount"}
                  direction={sortColumn === "amount" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("amount")}
                  className="font-medium"
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortColumn === "status"}
                  direction={sortColumn === "status" ? sortDirection : "asc"}
                  onClick={() => handleRequestSort("status")}
                  className="font-medium"
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell className="font-medium">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInvoices.map((invoice) => (
              <TableRow
                key={invoice.id}
                onClick={() => handleOpenInvoice(invoice)}
                className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                  invoice.status === "overdue"
                    ? "bg-red-50"
                    : invoice.status === "pending"
                    ? "bg-amber-50"
                    : ""
                }`}
              >
                <TableCell component="th" scope="row" className="font-medium">
                  {invoice.invoiceNumber}
                </TableCell>
                <TableCell>{invoice.customerName}</TableCell>
                <TableCell>
                  {getInvoiceTypeDisplay(invoice.invoiceType)}
                </TableCell>
                <TableCell>{invoice.issueDate}</TableCell>
                <TableCell>{invoice.dueDate}</TableCell>
                <TableCell>
                  $
                  {invoice.amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getOverdueText(invoice)}
                    color={getStatusColor(invoice.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="View Invoice">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenInvoice(invoice);
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invoice Detail Modal */}
      <InvoiceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        invoiceData={selectedInvoice}
      />
    </div>
  );
};

export default ManageInvoices;
